import { useCallback, useEffect, useRef, useState } from 'react';

import { usePageLock } from 'contexts/page-lock/usePageLock';
import { AvailableFeatureFlags } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

const usePublicationSettingsLock = () => {
  const currentProperty = useConvertedProperty();
  const { state: lockState, lockPageFragment, releasePageFragment, loading: lockLoading, refetcher } = usePageLock();
  const isPageLockingEnabled = useFeatureFlag(AvailableFeatureFlags.PublicationSettingsLockingEnabled, false);

  const [lockIsReady, setLockIsReady] = useState(false);
  const autolockAttemptedRef = useRef<string | null>(null);
  const publishingFragment = lockState.publishing;

  useEffect(() => {
    if (!isPageLockingEnabled) {
      setLockIsReady(true);
      return;
    }
    if (!lockLoading && currentProperty && autolockAttemptedRef.current !== currentProperty) {
      autolockAttemptedRef.current = currentProperty;
      lockPageFragment('publishing')
        .finally(() => {
          setLockIsReady(true);
        })
        .catch(console.error);
    }
  }, [isPageLockingEnabled, lockLoading, lockPageFragment, currentProperty]);

  const handleLock = useCallback(
    async (withForce: boolean) => {
      try {
        await lockPageFragment('publishing', withForce);
        if (!withForce) {
          refetcher?.();
        }
      } catch (error) {
        console.error('Error acquiring page lock:', error);
      }
      return null;
    },
    [lockPageFragment, refetcher]
  );

  const handleRelease = useCallback(
    async (withForce: boolean) => {
      try {
        await releasePageFragment('publishing', withForce);
        refetcher?.();
      } catch (error) {
        console.error('Error releasing page lock:', error);
      }
      return null;
    },
    [releasePageFragment, refetcher]
  );

  return {
    publishingFragment,
    lockIsReady,
    lockLoading,
    isPageLockingEnabled,
    handleLock,
    handleRelease
  };
};

export default usePublicationSettingsLock;
